import { useNavigate } from 'react-router'
import React from 'react'
import { APP_CONSTANTS } from 'src/consumer/constants'
import logo_brand from 'src/assets/logo_brand.png'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { Box, Typography } from '@mui/material'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import PersonIcon from '@mui/icons-material/Person'
import VideocamIcon from '@mui/icons-material/Videocam'
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom'

dayjs.extend(utc)
dayjs.extend(timezone)

export default function JoinPageHeader({ isMountedOnAWebView, userRole, bookedEventFinalData }) {
  const navigate = useNavigate()

  const isOffline = bookedEventFinalData?.meetingSrc === 'offline'
  const startTime = dayjs?.utc(bookedEventFinalData?.startTime).local()
  const endTime = dayjs?.utc(bookedEventFinalData?.endTime).local()

  return (
    <Box sx={{ width: '100%' }}>
      {isMountedOnAWebView && (
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Typography
            onClick={() => navigate('/webview/my-live-sessions')}
            sx={{
              color: '#6366f1',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              textDecoration: 'underline',
              '&:hover': { color: '#4f46e5' },
            }}
          >
            ← Back to Booked Sessions
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mb: { xs: 2.5, md: 3 },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            mb: 1.5,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%)',
              filter: 'blur(8px)',
            }}
          />
          <Box
            component="img"
            src={APP_CONSTANTS.APP_LOGO_LINK}
            alt="AUMHUM"
            onError={(e) => {
              e.target.src = logo_brand
            }}
            onClick={() => {
              if (!isMountedOnAWebView) {
                userRole === 'mentor' ? navigate('/mentor-home') : navigate('/')
              }
            }}
            sx={{
              position: 'relative',
              height: { xs: '56px', md: '64px' },
              width: { xs: '56px', md: '64px' },
              borderRadius: '16px',
              cursor: 'pointer',
              boxShadow: '0 4px 20px rgba(99, 102, 241, 0.2), 0 2px 8px rgba(0, 0, 0, 0.08)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: '0 6px 24px rgba(99, 102, 241, 0.3), 0 4px 12px rgba(0, 0, 0, 0.1)',
              },
            }}
          />
        </Box>

        <Typography
          sx={{
            fontSize: '12px',
            color: '#9ca3af',
            fontWeight: 500,
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
          }}
        >
          Your Session
        </Typography>
      </Box>

      {bookedEventFinalData && (
        <Box
          sx={{
            background: '#ffffff',
            borderRadius: { xs: '20px', md: '24px' },
            padding: { xs: '20px', md: '24px' },
            border: '1px solid rgba(99, 102, 241, 0.1)',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06), 0 1px 4px rgba(0, 0, 0, 0.04)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #8b5cf6 0%, #6366f1 50%, #3b82f6 100%)',
            }}
          />

          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              background: isOffline
                ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
                : 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
              border: `1px solid ${isOffline ? '#86efac' : '#93c5fd'}`,
              borderRadius: '20px',
              padding: '6px 12px',
              mb: 2,
            }}
          >
            {isOffline ? (
              <MeetingRoomIcon sx={{ fontSize: '16px', color: '#22c55e' }} />
            ) : (
              <VideocamIcon sx={{ fontSize: '16px', color: '#3b82f6' }} />
            )}
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 600,
                color: isOffline ? '#15803d' : '#1d4ed8',
                textTransform: 'uppercase',
                letterSpacing: '0.3px',
              }}
            >
              {isOffline ? 'In-Person' : 'Video Call'}
            </Typography>
          </Box>

          {bookedEventFinalData?.eventName && (
            <Typography
              sx={{
                fontSize: { xs: '18px', md: '20px' },
                fontWeight: 700,
                color: '#111827',
                mb: 2,
                lineHeight: 1.4,
              }}
            >
              {bookedEventFinalData.eventName}
            </Typography>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: { xs: '12px', md: '16px' },
            }}
          >
            <DetailItem
              icon={<CalendarTodayIcon sx={{ fontSize: '18px', color: '#8b5cf6' }} />}
              label="Date"
              value={startTime?.format('dddd, MMM D')}
            />

            <DetailItem
              icon={<AccessTimeIcon sx={{ fontSize: '18px', color: '#8b5cf6' }} />}
              label="Time"
              value={
                isOffline
                  ? startTime?.format('h:mm A')
                  : `${startTime?.format('h:mm A')} - ${endTime?.format('h:mm A')}`
              }
            />

            {bookedEventFinalData?.mentorName && (
              <DetailItem
                icon={<PersonIcon sx={{ fontSize: '18px', color: '#8b5cf6' }} />}
                label={isOffline ? 'Doctor' : 'With'}
                value={bookedEventFinalData.mentorName}
                fullWidth={!isOffline}
              />
            )}
          </Box>
        </Box>
      )}
    </Box>
  )
}

function DetailItem({ icon, label, value, fullWidth = false }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px',
        background: 'rgba(249, 250, 251, 0.8)',
        borderRadius: '14px',
        gridColumn: fullWidth ? { xs: 'auto', sm: '1 / -1' } : 'auto',
      }}
    >
      <Box
        sx={{
          width: '36px',
          height: '36px',
          borderRadius: '10px',
          background: 'rgba(139, 92, 246, 0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: '11px',
            color: '#9ca3af',
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            mb: 0.5,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: '14px',
            color: '#374151',
            fontWeight: 600,
            lineHeight: 1.4,
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  )
}
